// File: src/pages/targets/BulkEditCustomerTargets.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Form,
  Row,
  Input,
  Label,
  Button,
  Spinner,
} from "reactstrap";
import swal from "sweetalert";
import Multiselect from "multiselect-react-dropdown";
import axios from "../import/axiosConfig";

import {
  ProductListView,
  CreateCustomerList,
  _Get,
} from "../import/ApiEndPoint/ApiCalling";
import {
  All_Users_List,
  View_CustomerTarget_List,
} from "../import/ApiEndPoint/Api";

/* ---------------- Helpers ---------------- */
const toNum = (v) => (isFinite(+v) ? +v : 0);
const fmt2 = (n) => (isFinite(+n) ? (+n).toFixed(2) : "0.00");
const safeId = (x) =>
  typeof x === "object" ? x?._id || x?.sId || "" : x || "";

const lc = (s) =>
  String(s || "")
    .trim()
    .toLowerCase();

const normPin = (v) => {
  const s = String(v ?? "").replace(/\D/g, "");
  return s.length ? s.padStart(6, "0") : "";
};

const pickFirst = (...vals) => {
  for (const v of vals) {
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return "";
};

const getPartyPincode = (party) =>
  normPin(
    pickFirst(
      party?.Pincode,
      party?.pincode,
      party?.PostalCode,
      party?.postalCode,
      party?.Zip,
      party?.zip,
      party?.BillingAddress?.Pincode,
      party?.BillingAddress?.pincode,
      party?.billingAddress?.Pincode,
      party?.billingAddress?.pincode,
      party?.ShippingAddress?.Pincode,
      party?.ShippingAddress?.pincode,
      party?.shippingAddress?.Pincode,
      party?.shippingAddress?.pincode,
      party?.Address?.Pincode,
      party?.Address?.pincode,
    ),
  );

const getPartyLocation = (party) => {
  const city = pickFirst(
    party?.City,
    party?.city,
    party?.BillingAddress?.City,
    party?.billingAddress?.city,
    party?.ShippingAddress?.City,
    party?.shippingAddress?.city,
  );
  const district = pickFirst(
    party?.District,
    party?.district,
    party?.BillingAddress?.District,
    party?.billingAddress?.district,
  );
  const state = pickFirst(
    party?.State,
    party?.state,
    party?.BillingAddress?.State,
    party?.billingAddress?.state,
  );
  return { city: lc(city), district: lc(district), state: lc(state) };
};

const entryPin = (srv) =>
  normPin(pickFirst(srv?.pincode, srv?.PinCode, srv?.pin, srv?.zip));
const entryCity = (srv) => lc(pickFirst(srv?.city, srv?.City));
const entryDistrict = (srv) => lc(pickFirst(srv?.district, srv?.District));
const entryState = (srv) => lc(pickFirst(srv?.state, srv?.State));

const getUserDisplayName = (u) => {
  const fn = u?.firstName || u?.name || "";
  const ln = u?.lastName || "";
  return `${fn} ${ln}`.trim() || "—";
};

const resolveSalesPersonFromService = (party, allUsers) => {
  if (!party) return "";

  const salesPersons = (allUsers || []).filter(
    (u) => u?.rolename?.roleName === "Sales Person" && u?.status === "Active",
  );

  if (!salesPersons.length) return "";

  const pin = getPartyPincode(party);
  const loc = getPartyLocation(party);

  if (pin) {
    for (const sp of salesPersons) {
      const service = Array.isArray(sp?.service) ? sp.service : [];
      if (service.some((s) => entryPin(s) && entryPin(s) === pin)) {
        return sp?._id || "";
      }
    }
  }

  const hasLoc = loc.city || loc.district || loc.state;

  if (hasLoc) {
    for (const sp of salesPersons) {
      const service = Array.isArray(sp?.service) ? sp.service : [];

      if (
        service.some((s) => {
          const c = entryCity(s);
          const d = entryDistrict(s);
          const st = entryState(s);

          const cityOk = loc.city ? c === loc.city : true;
          const distOk = loc.district ? d === loc.district : true;
          const stateOk = loc.state ? st === loc.state : true;

          return cityOk && distOk && stateOk;
        })
      ) {
        return sp?._id || "";
      }
    }
  }

  return "";
};

/* ---------- Financial Year helpers ---------- */
const FY_MONTHS = [
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
  "January",
  "February",
  "March",
];

const CALENDAR_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const currentFYLabel = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = d.getMonth(); // 0=Jan
  const start = m >= 3 ? y : y - 1; // FY starts in April
  const endShort = String((start + 1) % 100).padStart(2, "0");
  return `${start}-${endShort}`;
};

const currentFYMonthName = () => CALENDAR_MONTHS[new Date().getMonth()];

const getFyStartYear = (fyLabel) => Number(String(fyLabel || "").split("-")[0]);

const monthYearForFY = (monthName, fyLabel) => {
  const startYear = parseInt(String(fyLabel).split("-")[0], 10);
  if (!Number.isFinite(startYear)) return "";

  const endYear = startYear + 1;
  const isLastQuarter = ["January", "February", "March"].includes(monthName);
  const year = isLastQuarter ? endYear : startYear;

  return `${monthName}-${year}`;
};

const parseMonthYear = (dateStr) => {
  if (!dateStr || typeof dateStr !== "string") {
    return { monthName: "", year: NaN };
  }

  const [m, y] = dateStr.split("-");
  return { monthName: m || "", year: Number(y) };
};

const fyLabelFromDate = (dateStr) => {
  const { monthName, year } = parseMonthYear(dateStr);
  if (!monthName || !Number.isFinite(year)) return currentFYLabel();

  const isLastQuarter = ["January", "February", "March"].includes(monthName);
  const start = isLastQuarter ? year - 1 : year;
  const endShort = String((start + 1) % 100).padStart(2, "0");

  return `${start}-${endShort}`;
};

const isPastMonthLocked = (fyLabel, monthName) => {
  const currentFy = currentFYLabel();
  const targetFyStart = getFyStartYear(fyLabel);
  const currentFyStart = getFyStartYear(currentFy);

  const targetMonthIndex = FY_MONTHS.indexOf(String(monthName || "").trim());
  const currentMonthIndex = FY_MONTHS.indexOf(currentFYMonthName());

  if (!Number.isFinite(targetFyStart)) return true;
  if (targetMonthIndex === -1) return true;
  if (targetFyStart < currentFyStart) return true;
  if (targetFyStart > currentFyStart) return false;

  return targetMonthIndex < currentMonthIndex;
};

const getEditablePeriod = (st = {}, doc = {}) => {
  const pickedMonth =
    st?.monthName || parseMonthYear(doc?.date)?.monthName || "";
  const pickedFy =
    st?.financialYear || fyLabelFromDate(doc?.date) || currentFYLabel();

  if (!pickedMonth || isPastMonthLocked(pickedFy, pickedMonth)) {
    return {
      fyear: currentFYLabel(),
      month: currentFYMonthName(),
      forcedToCurrentMonth: true,
    };
  }

  return {
    fyear: pickedFy,
    month: pickedMonth,
    forcedToCurrentMonth: false,
  };
};

/* ---------- API helpers ---------- */
// Backend function: updateCustomerTarget(req, res)
// If your route path is different, change only this value.
const UPDATE_CUSTOMER_TARGET_API = "/target/update-customer-target";

const callUpdateCustomerTarget = async (payload) => {
  try {
    return await axios.put(UPDATE_CUSTOMER_TARGET_API, payload);
  } catch (putErr) {
    const status = putErr?.response?.status;

    // Some projects register this controller as POST instead of PUT.
    // Fallback only for route/method mismatch, not for validation errors like locked month.
    if (status === 404 || status === 405) {
      return axios.post(UPDATE_CUSTOMER_TARGET_API, payload);
    }

    throw putErr;
  }
};

export default function BulkEditCustomerTargets() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [ProductList, setProductList] = useState([]);
  const [UserList, setUserList] = useState([]);
  const [AllUsers, setAllUsers] = useState([]);

  const [party, setParty] = useState(null);
  const [allTargetsForParty, setAllTargetsForParty] = useState([]);

  // docId -> { targetQtyMap, priceMap, incrementPercent, date, monthName, financialYear }
  const [perMonthState, setPerMonthState] = useState({});

  const [resolvedSalesPersonId, setResolvedSalesPersonId] = useState("");
  const [resolvedSalesPerson, setResolvedSalesPerson] = useState(null);

  const FY_OPTIONS = useMemo(() => {
    const curStart = parseInt(currentFYLabel().split("-")[0], 10);
    const arr = [];

    for (let y = curStart - 2; y <= curStart + 2; y++) {
      const endShort = String((y + 1) % 100).padStart(2, "0");
      arr.push(`${y}-${endShort}`);
    }

    return arr.reverse();
  }, []);

  useEffect(() => {
    (async () => {
      const u = JSON.parse(localStorage.getItem("userData") || "{}");

      if (!u?._id || !u?.database) {
        setLoading(false);
        return;
      }

      try {
        const [custRes, prodRes, usersRes] = await Promise.all([
          CreateCustomerList(u._id, u.database),
          ProductListView(u._id, u.database),
          _Get(All_Users_List, u?.database),
        ]);

        setUserList(custRes?.Customer || []);
        setProductList(prodRes?.Product || []);

        const list =
          usersRes?.User || usersRes?.users || usersRes?.adminDetails || [];

        setAllUsers(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error(e);
        swal("Error", "Failed to load data", "error");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const loadTargetsForParty = async (partyId) => {
    const u = JSON.parse(localStorage.getItem("userData") || "{}");
    const res = await _Get(View_CustomerTarget_List, u?.database);
    const allTargets = res?.data || [];
    const pid = safeId(partyId);

    const list = allTargets.filter(
      (t) => safeId(t?.partyId) === pid && t?.products?.length,
    );

    list.sort((a, b) => String(a?.date).localeCompare(String(b?.date)));

    setAllTargetsForParty(list);

    const st = {};

    list.forEach((doc) => {
      const tq = {};
      const pm = {};

      (doc?.products || []).forEach((p) => {
        const pid = safeId(p?.productId);
        tq[pid] = toNum(p?.qtyAssign);
        pm[pid] = toNum(p?.price);
      });

      const dateStr = doc?.date || "";
      const { monthName } = parseMonthYear(dateStr);
      const fyLabel = fyLabelFromDate(dateStr);

      st[doc?._id] = {
        targetQtyMap: tq,
        priceMap: pm,
        date: dateStr,
        monthName: monthName || "",
        financialYear: fyLabel || currentFYLabel(),
        incrementPercent:
          doc?.incrementPercent ??
          doc?.products?.[0]?.assignPercentage?.[0]?.percentage ??
          10,
      };
    });

    setPerMonthState(st);
  };

  const onSelectParty = (list, item) => {
    setParty(item);
    if (item?._id) loadTargetsForParty(item._id);
  };

  const onRemoveParty = () => {
    setParty(null);
    setAllTargetsForParty([]);
    setPerMonthState({});
    setResolvedSalesPersonId("");
    setResolvedSalesPerson(null);
  };

  useEffect(() => {
    if (party && AllUsers?.length) {
      const spId = resolveSalesPersonFromService(party, AllUsers);

      setResolvedSalesPersonId(spId);
      setResolvedSalesPerson(
        spId ? AllUsers.find((u) => u?._id === spId) || null : null,
      );
    } else {
      setResolvedSalesPersonId("");
      setResolvedSalesPerson(null);
    }
  }, [party, AllUsers]);

  const setTargetQty = (monthDocId, productId, value) => {
    const v = Math.max(toNum(value), 0);

    setPerMonthState((s) => ({
      ...s,
      [monthDocId]: {
        ...s[monthDocId],
        targetQtyMap: {
          ...(s[monthDocId]?.targetQtyMap || {}),
          [productId]: v,
        },
      },
    }));
  };

  const monthRows = (doc) => {
    const st = perMonthState[doc?._id] || {};

    return (doc?.products || []).map((p) => {
      const pid = safeId(p?.productId);
      const meta = ProductList.find((pp) => safeId(pp?._id) === pid) || null;
      const name =
        meta?.Product_Title || p?.productDetails?.Product_Title || "—";
      const price = toNum(st?.priceMap?.[pid] ?? p?.price ?? meta?.SalesRate);
      const targetQty = toNum(st?.targetQtyMap?.[pid]);
      const total = targetQty * price;

      return { _id: pid, name, price, targetQty, total };
    });
  };

  const monthGrandTotal = (doc) =>
    monthRows(doc).reduce((s, r) => s + (isFinite(r.total) ? r.total : 0), 0);

  const saveOne = async (doc) => {
    const st = perMonthState[doc?._id] || {};
    const rows = monthRows(doc);
    const user = JSON.parse(localStorage.getItem("userData") || "{}");
    const effectivePeriod = getEditablePeriod(st, doc);

    const productItem = rows
      .filter((r) => toNum(r.targetQty) > 0)
      .map((r) => {
        const total = Number((r.price * r.targetQty).toFixed(2));

        return {
          productId: r._id,
          ProductId: r._id,
          product_id: r._id,

          Product_Title: r.name,
          productName: r.name,
          name: r.name,
          title: r.name,

          qtyAssign: Number(r.targetQty),
          qty: Number(r.targetQty),
          targetQty: Number(r.targetQty),

          price: Number(r.price),
          rate: Number(r.price),
          salesRate: Number(r.price),

          totalPrice: total,
          total,
          target: total,
          amount: total,
          targetAmount: total,
          monthlyTarget: total,
          productTarget: total,
          currentTarget: total,
          newTarget: total,
        };
      });

    if (!user?.database) {
      throw new Error("Database not found in logged-in user data");
    }

    if (!party?._id && !safeId(doc?.partyId)) {
      throw new Error("Customer not selected");
    }

    if (!productItem.length) {
      throw new Error("Please enter target quantity for at least one product");
    }

    const payload = {
      database: user.database,
      fyear: effectivePeriod.fyear,
      month: effectivePeriod.month,
      customerId: party?._id || safeId(doc?.partyId),
      incrementper: Number(st?.incrementPercent || 0),
      productItem,
      created_by: user?._id,

      targetStatus: "updated",
      saveStatus: "updated",
      isEdited: true,
      isUpdated: true,
      updateUpperHierarchy: true,
      rollupAssignedHierarchy: true,
    };

    const response = await callUpdateCustomerTarget(payload);

    return {
      response,
      effectivePeriod,
    };
  };

  const saveAll = async () => {
    if (!allTargetsForParty.length) return;

    try {
      setSaving(true);

      const updatedLabels = [];

      for (const doc of allTargetsForParty) {
        const result = await saveOne(doc);
        updatedLabels.push(
          `${result?.effectivePeriod?.month || ""} ${
            result?.effectivePeriod?.fyear || ""
          }`.trim(),
        );
      }

      swal(
        "Saved all months successfully",
        updatedLabels.length
          ? `Updated through hierarchy: ${updatedLabels.join(", ")}`
          : "",
        "success",
      );

      if (party?._id) await loadTargetsForParty(party._id);
    } catch (e) {
      console.error(e);
      swal(
        "Error",
        e?.response?.data?.message ||
          e?.message ||
          "Failed to save some months",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary" />
      </div>
    );
  }

  return (
    <div>
      <Card>
        <Row className="m-2">
          <Col>
            <h1>Bulk Edit Customer Targets</h1>
          </Col>

          <Col className="d-flex justify-content-end" style={{ gap: 8 }}>
            <Button color="secondary" onClick={() => navigate(-1)}>
              Back
            </Button>

            <Button
              color="primary"
              onClick={saveAll}
              disabled={saving || !allTargetsForParty.length}
            >
              {saving ? "Saving..." : "Save All"}
            </Button>
          </Col>
        </Row>

        <CardBody>
          <Row>
            <Col className="mb-1" lg="4" md="6" sm="12">
              <Label>Select Party</Label>

              <Multiselect
                selectionLimit={1}
                isObject
                options={UserList}
                selectedValues={party ? [party] : []}
                onSelect={(l, item) => onSelectParty(l, item)}
                onRemove={onRemoveParty}
                displayValue="CompanyName"
              />
            </Col>

            <Col className="mb-1" lg="4" md="6" sm="12">
              <Label>Assigned Sales Person</Label>

              <Input
                type="text"
                readOnly
                disabled
                value={
                  resolvedSalesPerson
                    ? getUserDisplayName(resolvedSalesPerson)
                    : party?._id
                      ? "Not Mapped"
                      : ""
                }
              />
            </Col>
          </Row>

          {!party ? (
            <div style={{ color: "#6b7280" }}>
              Choose a party to edit months.
            </div>
          ) : allTargetsForParty.length === 0 ? (
            <div style={{ color: "#6b7280" }}>
              No target months saved for this party.
            </div>
          ) : (
            allTargetsForParty.map((doc) => {
              const st = perMonthState[doc?._id] || {};
              const rows = monthRows(doc);
              const gtot = monthGrandTotal(doc);
              const effectivePeriod = getEditablePeriod(st, doc);

              return (
                <Card key={doc?._id} style={{ marginTop: 16 }}>
                  <CardBody>
                    <Form
                      onSubmit={async (e) => {
                        e.preventDefault();

                        try {
                          setSaving(true);

                          const result = await saveOne(doc);

                          swal(
                            `Saved: ${result?.effectivePeriod?.month || ""} ${
                              result?.effectivePeriod?.fyear || ""
                            }`,
                            result?.effectivePeriod?.forcedToCurrentMonth
                              ? "Completed month was locked, so target was updated from current month automatically."
                              : "Customer target updated and rolled up to assigned upper hierarchy.",
                            "success",
                          );

                          if (party?._id) await loadTargetsForParty(party._id);
                        } catch (err) {
                          console.error(err);
                          swal(
                            "Error",
                            err?.response?.data?.message ||
                              err?.message ||
                              "Failed to save",
                            "error",
                          );
                        } finally {
                          setSaving(false);
                        }
                      }}
                    >
                      <Row>
                        <Col lg="3" md="4" sm="12" className="mb-1">
                          <Label>Financial Year</Label>

                          <Input
                            type="select"
                            value={st?.financialYear || currentFYLabel()}
                            onChange={(e) =>
                              setPerMonthState((s) => ({
                                ...s,
                                [doc?._id]: {
                                  ...s[doc?._id],
                                  financialYear: e.target.value,
                                },
                              }))
                            }
                          >
                            {FY_OPTIONS.map((fy) => (
                              <option key={fy} value={fy}>
                                {fy}
                              </option>
                            ))}
                          </Input>
                        </Col>

                        <Col lg="3" md="4" sm="12" className="mb-1">
                          <Label>Target Month (FY)</Label>

                          <Input
                            type="select"
                            value={st?.monthName || ""}
                            onChange={(e) =>
                              setPerMonthState((s) => ({
                                ...s,
                                [doc?._id]: {
                                  ...s[doc?._id],
                                  monthName: e.target.value,
                                },
                              }))
                            }
                          >
                            <option value="">Select Month</option>

                            {FY_MONTHS.map((m) => (
                              <option key={m} value={m}>
                                {m}
                              </option>
                            ))}
                          </Input>
                        </Col>

                        <Col lg="3" md="4" sm="12" className="mb-1">
                          <Label>Current Saved Date</Label>

                          <Input
                            type="text"
                            readOnly
                            value={st?.date || doc?.date || ""}
                          />
                        </Col>

                        <Col lg="3" md="4" sm="12" className="mb-1">
                          <Label>Increment %</Label>

                          <Input
                            type="number"
                            min="0"
                            value={st?.incrementPercent ?? 10}
                            onChange={(e) =>
                              setPerMonthState((s) => ({
                                ...s,
                                [doc?._id]: {
                                  ...s[doc?._id],
                                  incrementPercent: e.target.value,
                                },
                              }))
                            }
                          />
                        </Col>
                      </Row>

                      {effectivePeriod?.forcedToCurrentMonth ? (
                        <div
                          style={{
                            marginTop: 8,
                            padding: "8px 10px",
                            borderRadius: 8,
                            background: "#fff7ed",
                            border: "1px solid #fed7aa",
                            color: "#9a3412",
                            fontWeight: 700,
                          }}
                        >
                          Completed month is locked. On save, this target will
                          be updated from {effectivePeriod.month}{" "}
                          {effectivePeriod.fyear}.
                        </div>
                      ) : null}

                      <div className="table-responsive mt-2">
                        <table className="table table-sm">
                          <thead>
                            <tr>
                              <th style={{ minWidth: 220 }}>Product</th>
                              <th>Target Qty</th>
                              <th>Price</th>
                              <th>Line Total</th>
                            </tr>
                          </thead>

                          <tbody>
                            {rows.map((r) => (
                              <tr key={r._id}>
                                <td>{r.name}</td>

                                <td>
                                  <Input
                                    type="number"
                                    min="0"
                                    value={r.targetQty}
                                    onChange={(e) =>
                                      setTargetQty(
                                        doc?._id,
                                        r._id,
                                        e.target.value,
                                      )
                                    }
                                  />
                                </td>

                                <td>
                                  <Input
                                    type="number"
                                    readOnly
                                    value={r.price}
                                  />
                                </td>

                                <td>
                                  <Input
                                    type="number"
                                    readOnly
                                    value={fmt2(r.total)}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>

                          <tfoot>
                            <tr>
                              <th colSpan={3} className="text-right">
                                Grand Total
                              </th>

                              <th>
                                <Input
                                  type="number"
                                  readOnly
                                  value={fmt2(gtot)}
                                />
                              </th>
                            </tr>
                          </tfoot>
                        </table>
                      </div>

                      <Row className="mt-2">
                        <Col className="d-flex justify-content-end">
                          <Button
                            color="primary"
                            type="submit"
                            disabled={saving}
                          >
                            {saving ? "Saving..." : "Save This Month"}
                          </Button>
                        </Col>
                      </Row>
                    </Form>
                  </CardBody>
                </Card>
              );
            })
          )}
        </CardBody>
      </Card>
    </div>
  );
}
